import type { Language } from '@eformulieren/engine'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createShareClient } from './shareClient.js'
import type { ReadOverview } from './sharing.js'

/**
 * Het overzicht van `GET /delingen` voor het formulier zelf (audit delen G-05, G-16, G-19, R-69,
 * besluiten 5 en 6 van 30 oktober 2026): hoe de kijker binnenkwam (`mijnToegang`), wie hem uitnodigde,
 * en per persoon een label voor de aanwezigheid en de opmerkingen.
 *
 * **Aan de interne kant leest `useAdvice` dit overzicht al**, elke 30 seconden; daar doet deze hook
 * niets (`enabled` onwaar) en gebruikt de pagina die lezing. Aan de externe kant leest deze hook het bij
 * het opkomen, daarna elke 30 seconden zolang het tabblad zichtbaar is, en eerder als er iemand
 * verschijnt die het overzicht nog niet kent (`ask`), hooguit eens per tien seconden: elke lezing telt
 * mee in de begrenzing per gebruiker.
 */
export interface SharingOverviewOptions {
  readonly enabled: boolean
  readonly instantieId: string
  readonly taal: Language
  readonly headers: Readonly<Record<string, string>>
  readonly onSessionExpired: () => void
}

const POLL_MS = 30_000
const SOONEST_MS = 10_000

function logChain(chain: Readonly<Record<string, unknown>>): void {
  console.error('eformulieren: overzicht van de delingen niet gelezen', chain)
}

export function useSharingOverview(options: SharingOverviewOptions): {
  readonly overview: ReadOverview | undefined
  /** Een sleutel die het overzicht niet kent: lees het binnenkort opnieuw. */
  readonly ask: () => void
} {
  const { enabled, instantieId, taal: language } = options
  const headerKey = JSON.stringify(options.headers)
  const callbacks = useRef(options)
  callbacks.current = options
  const client = useMemo(
    () =>
      createShareClient({
        instantieId,
        headers: JSON.parse(headerKey) as Readonly<Record<string, string>>,
        taal: language,
        sectionName: () => undefined,
        fetch: (input, init) => globalThis.fetch(input, init),
        onSessionExpired: () => callbacks.current.onSessionExpired(),
        log: logChain,
      }),
    [instantieId, headerKey, language],
  )
  const [overview, setOverview] = useState<ReadOverview | undefined>(undefined)
  const last = useRef(0)
  const planned = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const read = useCallback(async (): Promise<void> => {
    last.current = Date.now()
    const outcome = await client.overview()
    if (outcome !== 'mislukt') setOverview(outcome)
  }, [client])

  useEffect(() => {
    setOverview(undefined)
    if (!enabled) return
    void read()
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') void read()
    }, POLL_MS)
    return () => {
      clearInterval(timer)
      if (planned.current !== undefined) clearTimeout(planned.current)
      planned.current = undefined
    }
  }, [enabled, read])

  const ask = useCallback(() => {
    if (!enabled || planned.current !== undefined) return
    const wait = Math.max(0, last.current + SOONEST_MS - Date.now())
    planned.current = setTimeout(() => {
      planned.current = undefined
      void read()
    }, wait)
  }, [enabled, read])

  return { overview, ask }
}
